/**
 * Settings → System → AI provider (ADR-004 §7, §7.1). A separate tab from the business settings, shown only to the
 * `settings.system` holder (system.admin — the founder); the server answers 403 to anyone else.
 * No prototype screen exists: this follows the Settings tab pattern (Integrations, Recordings and privacy) and the
 * design tokens. Everything binds to the live routes:
 *  - summary strip — GET /settings/llm (providers ready, the default) and GET /settings/llm/usage (calls, spend);
 *  - one card per provider — key state as last4 + who/when (never the key), the latest test, readiness and why not;
 *    set/replace a key (PUT …/secrets/{provider}, a write-only field cleared after save), remove it (DELETE, 409
 *    "still in use" in plain words), Test (POST /settings/llm/test, 202, then polled until the worker answers);
 *  - the per-purpose matrix — provider, model, output limit, timeout (PATCH /settings/llm with If-Match); a provider
 *    that is not ready is refused by the server and its reason is shown under the row;
 *  - spend ceiling and the staging stub (PATCH /settings/llm);
 *  - usage by provider, model and purpose — the Claude CLI as a call count labelled "not metered", APIs with cost.
 */
import { useMemo, useState } from 'react';
import {
  LLM_PROVIDER_LABEL,
  LLM_PURPOSES,
  LLM_PURPOSE_LABEL,
  type KeyedLlmProvider,
  type LlmCheck,
  type LlmProviderId,
  type LlmProviderStatus,
  type LlmPurpose,
  type LlmPurposeSetting,
  type LlmSettingsResponse,
  type LlmUsageQuery,
  type PatchLlmSettingsBody,
} from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import {
  anyRunning,
  claudeLoginChip,
  claudeLoginLine,
  fullDayLabel,
  daysUntil,
  RENEW_WARNING_DAYS,
  timeLabel,
  useClaudeLogin,
  useClaudeLogout,
  useCompleteClaudeLogin,
  useStartClaudeLogin,
  int,
  modelsFor,
  providerLabel,
  purposeLabel,
  useDeleteLlmKey,
  useLlmSettings,
  useLlmUsage,
  usePutLlmKey,
  useTestLlm,
  useUpdateLlm,
  usd,
  keyInUseText,
} from '../../lib/llm';
import { syncedAt } from '../../lib/money';
import { Empty, ErrorState, Loading, Modal, Seg, Switch, errorText, useToast } from '../../ui/kit';

const PROVIDERS = Object.keys(LLM_PROVIDER_LABEL) as LlmProviderId[];

/** A 412 means someone else saved Settings: show their version and say so. */
function useSaveFailure(refetch: () => unknown) {
  const toast = useToast();
  return (e: unknown) => {
    if (e instanceof ApiFailure && e.status === 412) {
      void refetch();
      toast('Someone else changed Settings just now. This page now shows their version: check it and try again.');
    } else toast(errorText(e));
  };
}

export function SystemAi() {
  const q = useLlmSettings();
  if (q.isPending) return <div className="panel"><Loading label="Loading the AI provider settings…" /></div>;
  if (q.isError) return <div className="panel"><ErrorState error={q.error} onRetry={() => void q.refetch()} /></div>;
  const s = q.data;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="note">
        <b>System settings.</b> These choose which AI provider runs every AI feature. A change applies from the next job, never one already
        running. Holding this tab gives no extra sight of clients, tasks, timesheets or revenue.
        {s.updated_at ? (
          <span style={{ display: 'block', marginTop: 4, fontSize: 12.5 }}>
            Last changed {syncedAt(s.updated_at)}
            {s.updated_by ? ` by ${s.updated_by.name}` : ''}.
          </span>
        ) : null}
      </div>
      {!s.key_storage_ready ? (
        <div className="note bad">
          <b>API keys and the Claude sign-in cannot be stored on this server.</b> It has no LLM_SECRET_KEK. Ask whoever runs the server to set
          it; until then only the Claude CLI can be used, with the CLAUDE_CODE_OAUTH_TOKEN set in the worker’s environment.
        </div>
      ) : null}
      <Summary s={s} />
      <div className="cols c3 llm-providers">
        {s.providers.map((p) => (
          <ProviderCard key={p.provider} p={p} keyStorage={s.key_storage_ready} />
        ))}
      </div>
      <Matrix key={s.config_version} s={s} refetch={() => q.refetch()} />
      <Limits key={`l${s.config_version}`} s={s} refetch={() => q.refetch()} />
      <Usage />
    </div>
  );
}

// ---------------------------------------------------------------- summary strip

function Summary({ s }: { s: LlmSettingsResponse }) {
  const u = useLlmUsage({});
  const ready = s.providers.filter((p) => p.ready).length;
  const dash = u.isPending ? '…' : u.isError ? '—' : null;
  const ceiling = s.daily_spend_ceiling_usd;
  const today = u.data?.today_metered_cost_usd ?? 0;
  const over = ceiling !== null && today >= ceiling;
  return (
    <div className="stats" style={{ marginBottom: 0 }}>
      <div className={`stat ${ready === 0 ? 'red' : ready < s.providers.length ? 'amber' : 'green'}`}>
        <div className="k">Providers ready</div>
        <div className="v">
          {ready} of {s.providers.length}
        </div>
        <div className="s">Configured and passed a test</div>
      </div>
      <div className="stat">
        <div className="k">Default provider</div>
        <div className="v" style={{ fontSize: 18, marginTop: 10 }}>{LLM_PROVIDER_LABEL[s.default_provider]}</div>
        <div className="s">For any use without its own choice</div>
      </div>
      <div className="stat">
        <div className="k">AI calls this month</div>
        <div className="v">{dash ?? int(u.data!.totals.calls)}</div>
        <div className="s">{dash ? 'Usage did not load' : `${int(u.data!.totals.not_metered_calls)} not metered (subscription)`}</div>
      </div>
      <div className="stat">
        <div className="k">Metered spend this month</div>
        <div className="v">{dash ?? usd(u.data!.totals.metered_cost_usd)}</div>
        <div className="s">API calls only</div>
      </div>
      <div className={`stat ${over ? 'red' : ''}`}>
        <div className="k">Spent today</div>
        <div className="v">{dash ?? usd(today)}</div>
        <div className="s">{ceiling === null ? 'No daily ceiling' : over ? `At the ${usd(ceiling)} ceiling: metered calls stop` : `Ceiling ${usd(ceiling)} a day`}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- provider cards

function checkChip(p: LlmProviderStatus): [string, string] {
  if (p.last_check?.status === 'running') return ['info', 'Testing…'];
  if (p.ready) return ['hot', 'Ready'];
  if (!p.configured) return ['', p.needs_key ? 'No key' : 'Not set up'];
  return ['warm', 'Not ready'];
}

function CheckLine({ c, keySetAt }: { c: LlmCheck | null; keySetAt: string | null }) {
  if (!c) return <div className="sub" style={{ color: 'var(--sub)' }}>Never tested.</div>;
  const by = c.requested_by ? ` by ${c.requested_by.name}` : '';
  // A result from before the current key was set says nothing about that key.
  const stale = keySetAt && c.status !== 'running' && c.requested_at < keySetAt ? ' (with the previous key)' : '';
  if (c.status === 'running')
    return (
      <div className="sub" style={{ color: 'var(--sub)' }}>
        Testing {c.model}, asked {syncedAt(c.requested_at)}
        {by}. The result appears here when the worker answers.
      </div>
    );
  if (c.status === 'failed')
    return (
      <div className="note bad" style={{ fontSize: 12.5, overflowWrap: 'anywhere' }}>
        <b>Test failed</b> {syncedAt(c.finished_at ?? c.requested_at)}
        {by}
        {stale}: {c.error ?? 'the provider did not say why.'}
      </div>
    );
  return (
    <div className="note good" style={{ fontSize: 12.5 }}>
      <b>Test passed</b> {syncedAt(c.finished_at ?? c.requested_at)}
      {by}
      {stale}
      {c.latency_ms !== null ? ` · ${int(c.latency_ms)} ms` : ''}
      {c.model_answered ? ` · answered by ${c.model_answered}` : ''}
      {c.json_valid === false ? ' · the answer did not match the expected format' : c.json_valid ? ' · answer in the expected format' : ''}
    </div>
  );
}

function ProviderCard({ p, keyStorage }: { p: LlmProviderStatus; keyStorage: boolean }) {
  const toast = useToast();
  const put = usePutLlmKey();
  const del = useDeleteLlmKey();
  const test = useTestLlm();
  const [key, setKey] = useState('');
  const [keyErr, setKeyErr] = useState<string | null>(null);
  const [delErr, setDelErr] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [chipCls, chipLabel] = checkChip(p);
  const running = p.last_check?.status === 'running';
  const keyed = p.needs_key ? (p.provider as KeyedLlmProvider) : null;
  // "The last test failed: <error>" repeats the test line above it; say it once.
  const repeatsCheck = p.last_check?.status === 'failed' && /^the last test failed/i.test(p.not_ready_reason ?? '');

  const saveKey = () => {
    if (!keyed) return;
    setKeyErr(null);
    put.mutate(
      { provider: keyed, api_key: key.trim() },
      {
        onSuccess: (r) => {
          setKey('');
          toast(`${p.label} key saved (ending ${r.key?.last4 ?? '····'}). Run a test before using it.`);
        },
        onError: (e) => {
          const fe = e instanceof ApiFailure ? e.fieldErrors.api_key : undefined;
          setKeyErr(fe ?? errorText(e));
        },
      },
    );
  };
  const removeKey = () => {
    if (!keyed) return;
    setConfirmDel(false);
    setDelErr(null);
    del.mutate(keyed, {
      onSuccess: () => toast(`${p.label} key removed.`),
      onError: (e) =>
        setDelErr(e instanceof ApiFailure && e.status === 409 ? keyInUseText(p.label, p.in_use_by) : errorText(e)),
    });
  };
  const runTest = () =>
    test.mutate(
      { provider: p.provider },
      { onSuccess: () => toast(`Testing ${p.label}…`), onError: (e) => toast(errorText(e)) },
    );

  return (
    <div className="panel" style={{ alignSelf: 'start', minWidth: 0 }} data-provider={p.provider}>
      <div className="hd">
        <h3>{p.label}</h3>
        <div className="r">
          <span className={`chip ${chipCls}`}>{chipLabel}</span>
        </div>
      </div>
      <div className="bd" style={{ display: 'grid', gap: 12 }}>
        <div>
          <div className="lbl" style={{ marginBottom: 4 }}>Used by</div>
          <div style={{ fontSize: 13 }}>
            {p.in_use_by.length ? p.in_use_by.map(purposeLabel).join(', ') : <span style={{ color: 'var(--sub)' }}>Nothing uses it now.</span>}
          </div>
        </div>

        {p.provider === 'claude_cli' ? (
          <ClaudeSignIn p={p} />
        ) : (
        <div>
          <div className="lbl" style={{ marginBottom: 4 }}>API key</div>
          {p.key ? (
            <div style={{ fontSize: 13 }}>
              <span className="mono">••••{p.key.last4}</span>
              <span className="sub" style={{ color: 'var(--sub)' }}>
                {' '}
                · set {syncedAt(p.key.set_at)}
                {p.key.set_by ? ` by ${p.key.set_by.name}` : ''}
              </span>
            </div>
          ) : (
            <div className="sub" style={{ color: 'var(--sub)', fontSize: 13 }}>No key yet.</div>
          )}
          {keyed ? (
            <form
              className="split"
              style={{ marginTop: 8 }}
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                if (key.trim()) saveKey();
              }}
            >
              <input
                className="in grow"
                type="password"
                autoComplete="new-password"
                spellCheck={false}
                aria-label={`${p.label} API key`}
                placeholder={p.key ? 'Paste a new key to replace it' : 'Paste the API key'}
                value={key}
                disabled={!keyStorage || put.isPending}
                onChange={(e) => {
                  setKey(e.target.value);
                  setKeyErr(null);
                }}
              />
              <button className="btn sm pri" type="submit" disabled={!keyStorage || !key.trim() || put.isPending}>
                {put.isPending ? 'Saving…' : p.key ? 'Replace' : 'Save key'}
              </button>
            </form>
          ) : null}
          {keyErr ? <small className="ferr" style={{ display: 'block', marginTop: 4 }}>{keyErr}</small> : null}
          {keyed && !keyStorage ? (
            <small className="fhint" style={{ display: 'block', marginTop: 4 }}>Keys cannot be stored until the server has LLM_SECRET_KEK.</small>
          ) : null}
        </div>
        )}

        <div>
          <div className="lbl" style={{ marginBottom: 4 }}>Latest test</div>
          <CheckLine c={p.last_check} keySetAt={p.key?.set_at ?? null} />
        </div>

        {!p.ready && p.not_ready_reason && !running && !repeatsCheck ? <div className="note warn" style={{ fontSize: 12.5, overflowWrap: 'anywhere' }}>{p.not_ready_reason}</div> : null}
        {delErr ? (
          <div className="note bad" role="alert" style={{ fontSize: 12.5 }}>
            {delErr}
          </div>
        ) : null}

        <div className="split">
          <button className="btn sm" disabled={running || test.isPending || !p.configured} onClick={runTest}>
            {running ? 'Testing…' : 'Test'}
          </button>
          <div className="grow" />
          {keyed && p.key ? (
            <button className="btn sm bad" disabled={del.isPending} onClick={() => setConfirmDel(true)}>
              Remove key
            </button>
          ) : null}
        </div>
      </div>

      {confirmDel ? (
        <Modal
          title={`Remove the ${p.label} key?`}
          sub={
            p.in_use_by.length
              ? `${p.in_use_by.map(purposeLabel).join(', ')} still use ${p.label}. The server will refuse until they are moved to another provider.`
              : 'Nothing uses this provider now. It cannot be chosen again until a new key is added and tested.'
          }
          onClose={() => setConfirmDel(false)}
          footer={
            <>
              <div className="grow" />
              <button className="btn" onClick={() => setConfirmDel(false)}>
                Cancel
              </button>
              <button className="btn bad" onClick={removeKey}>
                Remove key
              </button>
            </>
          }
        >
          {null}
        </Modal>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Claude CLI sign-in

/**
 * The Claude CLI card's sign-in block (claudeLogin.*): the founder signs in to Claude here instead of pasting
 * CLAUDE_CODE_OAUTH_TOKEN into the server environment. Sign in → open claude.ai → paste the code it shows within ten
 * minutes → the token is stored (only its last four characters are ever shown) → Test. The open sign-in survives a
 * reload: GET returns it in `pending`. Signing out falls back to the environment token, if one is set.
 */
function ClaudeSignIn({ p }: { p: LlmProviderStatus }) {
  const toast = useToast();
  const q = useClaudeLogin();
  const start = useStartClaudeLogin();
  const complete = useCompleteClaudeLogin();
  const logout = useClaudeLogout();
  const [code, setCode] = useState('');
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [startErr, setStartErr] = useState<string | null>(null);
  const [confirmOut, setConfirmOut] = useState(false);

  const head = (chip?: [string, string]) => (
    <div className="split" style={{ marginBottom: 4 }}>
      <div className="lbl">Claude sign-in</div>
      <div className="grow" />
      {chip ? <span className={`chip ${chip[0]}`} data-testid="claude-login-chip">{chip[1]}</span> : null}
    </div>
  );
  if (q.isPending)
    return (
      <div>
        {head()}
        <div className="sub" style={{ color: 'var(--sub)', fontSize: 13 }}>Checking the Claude sign-in…</div>
      </div>
    );
  if (q.isError)
    return (
      <div>
        {head()}
        <div className="note bad" role="alert" style={{ fontSize: 12.5 }}>
          The sign-in status did not load: {errorText(q.error)}{' '}
          <button className="btn sm" onClick={() => void q.refetch()}>Try again</button>
        </div>
      </div>
    );

  const s = q.data;
  const pending = s.pending;
  const stored = s.last4 !== null;
  const canStore = s.key_storage_ready;
  const soon = s.state === 'signed_in' && s.expires_at !== null && daysUntil(s.expires_at) < RENEW_WARNING_DAYS;

  const begin = () => {
    setStartErr(null);
    setCodeErr(null);
    setCode('');
    start.mutate(undefined, {
      onSuccess: (r) => {
        // This runs after the POST answers, not in the click itself, so a browser may block the new tab (Safari does).
        // That is why the pending panel always carries the "Open claude.ai and sign in" link, and the toast names it.
        window.open(r.auth_url, '_blank', 'noopener,noreferrer');
        toast('Sign in on claude.ai (if no tab opened, use the “Open claude.ai and sign in” link), then paste the code it shows here.');
      },
      onError: (e) => setStartErr(errorText(e)),
    });
  };
  const finish = () => {
    if (!pending) return;
    setCodeErr(null);
    complete.mutate(
      { login_id: pending.login_id, code: code.trim() },
      {
        onSuccess: (r) => {
          setCode('');
          toast(`Signed in to Claude (token ending ${r.last4 ?? '····'}). Run a test before relying on it.`);
        },
        onError: (e) => {
          const fe = e instanceof ApiFailure ? e.fieldErrors : {};
          setCodeErr(fe.code ?? fe.login_id ?? errorText(e));
        },
      },
    );
  };
  const signOut = () => {
    setConfirmOut(false);
    logout.mutate(undefined, {
      onSuccess: () => toast('Signed out of Claude here. Test the Claude CLI to see whether the environment token still works.'),
      onError: (e) => toast(errorText(e)),
    });
  };

  return (
    <div data-testid="claude-login" data-state={s.state}>
      {head(claudeLoginChip(s))}

      {stored ? (
        <div style={{ fontSize: 13 }}>
          <span className="mono">••••{s.last4}</span>
          <span className="sub" style={{ color: 'var(--sub)' }}> · {claudeLoginLine(s)}</span>
        </div>
      ) : (
        <div className="sub" style={{ color: 'var(--sub)', fontSize: 13 }}>
          Not signed in here. Until someone signs in, the Claude CLI uses CLAUDE_CODE_OAUTH_TOKEN from the worker’s environment, if one is set.
          Its calls run on that Claude subscription, so they are counted but not metered.
        </div>
      )}

      {(s.state === 'expired' || s.state === 'error') && s.error ? (
        <div className="note bad" style={{ fontSize: 12.5, marginTop: 8, overflowWrap: 'anywhere' }}>{s.error}</div>
      ) : null}
      {soon && s.expires_at ? (
        <div className="note warn" style={{ fontSize: 12.5, marginTop: 8 }}>
          This sign-in stops working on {fullDayLabel(s.expires_at)}. Sign in again before then to renew it for a year.
        </div>
      ) : null}

      {pending ? (
        <div className="note" style={{ marginTop: 8, display: 'grid', gap: 8, fontSize: 12.5 }} data-testid="claude-login-pending">
          <div>
            <b>1.</b>{' '}
            <a href={pending.auth_url} target="_blank" rel="noopener noreferrer">
              Open claude.ai and sign in
            </a>{' '}
            with the Claude account whose subscription the Claude CLI should use.
          </div>
          <div>
            <b>2.</b> Paste the code claude.ai shows, by {timeLabel(pending.expires_at)}.
          </div>
          <form
            className="split"
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              if (code.trim()) finish();
            }}
          >
            <input
              className="in grow mono"
              type="password"
              autoComplete="one-time-code"
              spellCheck={false}
              aria-label="Code from claude.ai"
              placeholder="Paste the code from claude.ai"
              value={code}
              disabled={complete.isPending}
              onChange={(e) => {
                setCode(e.target.value);
                setCodeErr(null);
              }}
            />
            <button className="btn sm pri" type="submit" disabled={!code.trim() || complete.isPending}>
              {complete.isPending ? 'Signing in…' : 'Finish signing in'}
            </button>
          </form>
          {codeErr ? <small className="ferr" role="alert">{codeErr}</small> : null}
          {pending && s.error && !codeErr ? <small className="ferr">{s.error}</small> : null}
          <small className="fhint">
            {stored ? 'The current sign-in keeps working until this one finishes. ' : ''}
            Not finished by {timeLabel(pending.expires_at)}, this sign-in closes by itself.
          </small>
        </div>
      ) : null}

      {startErr ? (
        <div className="note bad" role="alert" style={{ fontSize: 12.5, marginTop: 8 }}>{startErr}</div>
      ) : null}
      {!canStore ? (
        <small className="fhint" style={{ display: 'block', marginTop: 4 }}>
          Signing in needs the server to have LLM_SECRET_KEK, so the token can be stored encrypted.
        </small>
      ) : null}

      <div className="split" style={{ marginTop: 8 }}>
        <button className={`btn sm ${pending || (s.state === 'signed_in' && !soon) ? '' : 'pri'}`} disabled={!canStore || start.isPending} onClick={begin}>
          {start.isPending ? 'Starting…' : pending ? 'Start again' : stored ? 'Sign in again' : 'Sign in to Claude'}
        </button>
        <div className="grow" />
        {stored ? (
          <button className="btn sm bad" disabled={logout.isPending} onClick={() => setConfirmOut(true)}>
            {logout.isPending ? 'Signing out…' : 'Sign out'}
          </button>
        ) : null}
      </div>

      {confirmOut ? (
        <Modal
          title="Sign the Claude CLI out here?"
          sub={
            (p.in_use_by.length ? `${p.in_use_by.map(purposeLabel).join(', ')} run on the Claude CLI. ` : '') +
            'The stored token is forgotten on this server (Claude itself is not told). The worker then falls back to CLAUDE_CODE_OAUTH_TOKEN from its environment, if one is set; without it, Claude CLI calls fail until someone signs in again.'
          }
          onClose={() => setConfirmOut(false)}
          footer={
            <>
              <div className="grow" />
              <button className="btn" onClick={() => setConfirmOut(false)}>
                Cancel
              </button>
              <button className="btn bad" onClick={signOut}>
                Sign out
              </button>
            </>
          }
        >
          {null}
        </Modal>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- the per-purpose matrix

type Draft = { default_provider?: LlmProviderId; purposes: Partial<Record<LlmPurpose, Partial<LlmPurposeSetting>>> };

function Matrix({ s, refetch }: { s: LlmSettingsResponse; refetch: () => unknown }) {
  const save = useUpdateLlm();
  const toast = useToast();
  const failed = useSaveFailure(refetch);
  const [draft, setDraft] = useState<Draft>({ purposes: {} });
  const [errs, setErrs] = useState<Record<string, string>>({});
  const byId = useMemo(() => new Map(s.providers.map((p) => [p.provider, p])), [s.providers]);

  const cur = (p: LlmPurpose): LlmPurposeSetting => ({ ...s.purposes[p], ...draft.purposes[p] });
  const defaultProvider = draft.default_provider ?? s.default_provider;

  /** Only what differs from the server goes in the PATCH. */
  const body = useMemo(() => {
    const b: PatchLlmSettingsBody = {};
    if (draft.default_provider && draft.default_provider !== s.default_provider) b.default_provider = draft.default_provider;
    const purposes: NonNullable<PatchLlmSettingsBody['purposes']> = {};
    for (const p of LLM_PURPOSES) {
      const d = draft.purposes[p];
      if (!d) continue;
      const changed = Object.fromEntries(Object.entries(d).filter(([k, v]) => s.purposes[p][k as keyof LlmPurposeSetting] !== v));
      if (Object.keys(changed).length) purposes[p] = changed;
    }
    if (Object.keys(purposes).length) b.purposes = purposes;
    return b;
  }, [draft, s]);
  const dirty = Object.keys(body).length > 0;

  const edit = (p: LlmPurpose, patch: Partial<LlmPurposeSetting>) => {
    setErrs({});
    setDraft((d) => ({ ...d, purposes: { ...d.purposes, [p]: { ...d.purposes[p], ...patch } } }));
  };
  const pickProvider = (p: LlmPurpose, provider: LlmProviderId) => {
    const models = modelsFor(s, provider);
    const model = cur(p).model;
    // A model belongs to one provider: switching provider takes its first allowed model unless the current one fits.
    edit(p, { provider, model: models.includes(model) ? model : (models[0] ?? model) });
  };
  const submit = () =>
    save.mutate(
      { version: s.config_version, body },
      {
        onSuccess: () => toast('Saved. The next AI job uses these settings.'),
        onError: (e) => {
          if (e instanceof ApiFailure && e.status === 422) {
            setErrs(e.fieldErrors);
            toast(e.message);
          } else failed(e);
        },
      },
    );

  const providerOptions = (selected: LlmProviderId) =>
    PROVIDERS.map((id) => {
      const st = byId.get(id);
      const tag = st && !st.ready && id !== selected ? ' (not ready)' : '';
      return (
        <option key={id} value={id}>
          {LLM_PROVIDER_LABEL[id]}
          {tag}
        </option>
      );
    });
  const rowErr = (p: LlmPurpose) =>
    Object.entries(errs)
      .filter(([k]) => k.startsWith(`purposes.${p}`))
      .map(([, v]) => v);

  return (
    <div className="panel">
      <div className="hd">
        <h3>Which provider runs what</h3>
        <div className="r">
          {dirty ? (
            <button className="btn sm" disabled={save.isPending} onClick={() => (setDraft({ purposes: {} }), setErrs({}))}>
              Discard
            </button>
          ) : null}
          <button className="btn sm pri" disabled={!dirty || save.isPending} onClick={submit}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
      <table className="llm-matrix">
        <thead>
          <tr>
            <th>Use</th>
            <th>Provider</th>
            <th>Model</th>
            <th>Longest answer (tokens)</th>
            <th>Give up after (seconds)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <b>Default</b>
              <div className="sub" style={{ color: 'var(--sub)', fontSize: 12 }}>Any use without its own row</div>
            </td>
            <td>
              <select
                className="in"
                aria-label="Default provider"
                value={defaultProvider}
                disabled={save.isPending}
                onChange={(e) => (setErrs({}), setDraft((d) => ({ ...d, default_provider: e.target.value as LlmProviderId })))}
              >
                {providerOptions(s.default_provider)}
              </select>
              {errs.default_provider ? <small className="ferr" role="alert" style={{ display: 'block', marginTop: 4, maxWidth: 260 }}>{errs.default_provider}</small> : null}
            </td>
            <td colSpan={3} className="sub" style={{ color: 'var(--sub)', fontSize: 12.5 }}>
              Model and limits are set per use below.
            </td>
          </tr>
          {LLM_PURPOSES.map((p) => {
            const c = cur(p);
            const models = modelsFor(s, c.provider);
            const errors = rowErr(p);
            const edited = !!body.purposes?.[p];
            return (
              <tr key={p} data-purpose={p}>
                <td>
                  <b>{LLM_PURPOSE_LABEL[p]}</b>
                  {edited ? <span className="chip brand" style={{ marginLeft: 6 }}>Changed</span> : null}
                  {errors.length ? (
                    <small className="ferr" role="alert" style={{ display: 'block', marginTop: 4, maxWidth: 260 }}>
                      {errors.join(' ')}
                    </small>
                  ) : null}
                </td>
                <td>
                  <select
                    className="in"
                    aria-label={`Provider for ${LLM_PURPOSE_LABEL[p]}`}
                    value={c.provider}
                    disabled={save.isPending}
                    onChange={(e) => pickProvider(p, e.target.value as LlmProviderId)}
                  >
                    {providerOptions(s.purposes[p].provider)}
                  </select>
                </td>
                <td>
                  <select
                    className="in"
                    aria-label={`Model for ${LLM_PURPOSE_LABEL[p]}`}
                    value={c.model}
                    disabled={save.isPending || !models.length}
                    onChange={(e) => edit(p, { model: e.target.value })}
                  >
                    {!models.includes(c.model) ? <option value={c.model}>{c.model} (not in the list)</option> : null}
                    {models.map((m) => (
                      <option key={m} value={m}>
                        {m === 'default' ? 'default (the CLI chooses)' : m}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    className="in"
                    type="number"
                    min={64}
                    max={32000}
                    step={64}
                    style={{ maxWidth: 120 }}
                    aria-label={`Longest answer for ${LLM_PURPOSE_LABEL[p]}`}
                    value={c.max_output_tokens}
                    disabled={save.isPending}
                    onChange={(e) => edit(p, { max_output_tokens: Number(e.target.value) })}
                  />
                </td>
                <td>
                  <input
                    className="in"
                    type="number"
                    min={5}
                    max={600}
                    style={{ maxWidth: 100 }}
                    aria-label={`Timeout for ${LLM_PURPOSE_LABEL[p]}`}
                    value={Math.round(c.timeout_ms / 1000)}
                    disabled={save.isPending}
                    onChange={(e) => edit(p, { timeout_ms: Number(e.target.value) * 1000 })}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="bd sub" style={{ color: 'var(--sub)', fontSize: 12.5, borderTop: '1px solid var(--line)' }}>
        Only a ready provider can be chosen: it needs a key (the CLI does not) and a passed test since the key was last set. The server says
        what is missing when it refuses.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- spend ceiling and staging stub

function Limits({ s, refetch }: { s: LlmSettingsResponse; refetch: () => unknown }) {
  const save = useUpdateLlm();
  const toast = useToast();
  const failed = useSaveFailure(refetch);
  const [ceiling, setCeiling] = useState(s.daily_spend_ceiling_usd === null ? '' : String(s.daily_spend_ceiling_usd));
  const [err, setErr] = useState<string | null>(null);
  const parsed = ceiling.trim() === '' ? null : Number(ceiling);
  const invalid = parsed !== null && (!Number.isFinite(parsed) || parsed < 0 || parsed > 10_000);
  const changed = !invalid && parsed !== s.daily_spend_ceiling_usd;

  const patch = (body: PatchLlmSettingsBody, done: string) =>
    save.mutate(
      { version: s.config_version, body },
      {
        onSuccess: () => toast(done),
        onError: (e) => {
          if (e instanceof ApiFailure && e.status === 422) setErr(Object.values(e.fieldErrors)[0] ?? e.message);
          else failed(e);
        },
      },
    );

  return (
    <div className="cols c2">
      <div className="panel">
        <div className="hd">
          <h3>Daily spend ceiling</h3>
        </div>
        <div className="bd">
          <div className="sub" style={{ color: 'var(--sub)', fontSize: 12.5, marginBottom: 8 }}>
            Metered API calls stop for the rest of the day (India time) once this much has been spent. The Claude CLI is not metered and is not
            stopped. Leave it empty for no ceiling.
          </div>
          <form
            className="split"
            onSubmit={(e) => {
              e.preventDefault();
              if (!changed) return;
              setErr(null);
              patch(
                { daily_spend_ceiling_usd: parsed },
                parsed === null ? 'Saved. There is no daily ceiling now.' : `Saved. Metered calls stop at ${usd(parsed)} a day.`,
              );
            }}
          >
            <span>$</span>
            <input
              className="in"
              style={{ maxWidth: 140 }}
              inputMode="decimal"
              aria-label="Daily spend ceiling in US dollars"
              placeholder="No ceiling"
              value={ceiling}
              disabled={save.isPending}
              onChange={(e) => (setCeiling(e.target.value), setErr(null))}
            />
            <span className="sub" style={{ color: 'var(--sub)' }}>a day</span>
            <div className="grow" />
            <button className="btn sm pri" type="submit" disabled={!changed || save.isPending}>
              Save
            </button>
          </form>
          {invalid ? <small className="ferr" style={{ display: 'block', marginTop: 4 }}>Enter an amount from 0 to 10,000, or leave it empty.</small> : null}
          {err ? <small className="ferr" style={{ display: 'block', marginTop: 4 }}>{err}</small> : null}
        </div>
      </div>
      <div className="panel">
        <div className="hd">
          <h3>Staging stub</h3>
        </div>
        <div className="bd split">
          <div className="grow sub" style={{ color: 'var(--sub)', fontSize: 12.5 }}>
            On staging, every use runs on a stub that calls no model and costs nothing. Production ignores this switch.
          </div>
          <Switch
            on={s.stub_in_staging}
            disabled={save.isPending}
            label="Use the stub on staging"
            onClick={() =>
              patch(
                { stub_in_staging: !s.stub_in_staging },
                s.stub_in_staging ? 'Saved. Staging now calls the providers chosen above.' : 'Saved. Staging now runs every use on the stub.',
              )
            }
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- usage

type Period = 'month' | 'last';
const istToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
function periodQuery(p: Period): LlmUsageQuery {
  if (p === 'month') return {};
  const [y, m] = istToday().split('-').map(Number) as [number, number];
  const py = m === 1 ? y - 1 : y;
  const pm = m === 1 ? 12 : m - 1;
  const last = new Date(Date.UTC(py, pm, 0)).getUTCDate();
  const mm = String(pm).padStart(2, '0');
  return { from: `${py}-${mm}-01`, to: `${py}-${mm}-${String(last).padStart(2, '0')}` };
}
const dayLabel = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

function Usage() {
  const [period, setPeriod] = useState<Period>('month');
  const u = useLlmUsage(periodQuery(period));
  return (
    <div className="panel">
      <div className="hd">
        <h3>Usage</h3>
        <div className="r">
          {u.data ? (
            <span>
              {dayLabel(u.data.from)} to {dayLabel(u.data.to)}
            </span>
          ) : null}
          <Seg
            options={[
              ['month', 'This month'],
              ['last', 'Last month'],
            ]}
            value={period}
            onChange={setPeriod}
          />
        </div>
      </div>
      {u.isPending ? (
        <Loading label="Loading usage…" />
      ) : u.isError ? (
        <ErrorState error={u.error} onRetry={() => void u.refetch()} />
      ) : u.data.rows.length === 0 ? (
        <Empty title="No AI calls in this period">Calls appear here as notes, emails and meetings are processed, and when a provider is tested.</Empty>
      ) : (
        <>
          <table className="llm-usage">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Model</th>
                <th>Use</th>
                <th className="num">Calls</th>
                <th className="num">Failed</th>
                <th className="num">Tokens in / out</th>
                <th className="num">Cost</th>
              </tr>
            </thead>
            <tbody>
              {u.data.rows.map((r) => (
                <tr key={`${r.provider}|${r.model}|${r.purpose}`}>
                  <td>{providerLabel(r.provider)}</td>
                  <td className="mono" style={{ fontSize: 12.5 }}>{r.model}</td>
                  <td>{purposeLabel(r.purpose)}</td>
                  <td className="num">{int(r.calls)}</td>
                  <td className="num" style={{ color: r.failed ? 'var(--cold)' : undefined }}>
                    {int(r.failed)}
                    {r.repaired ? <span className="sub" style={{ color: 'var(--sub)' }}> · {int(r.repaired)} repaired</span> : null}
                  </td>
                  <td className="num">
                    {int(r.input_tokens)} / {int(r.output_tokens)}
                  </td>
                  <td className="num">
                    {r.cost_usd !== null ? (
                      <>
                        {usd(r.cost_usd)}
                        {r.cost_basis === 'mixed' ? <span className="sub" style={{ color: 'var(--sub)' }}> (part not metered)</span> : null}
                      </>
                    ) : (
                      <span className="chip">{r.label ?? (r.cost_basis === 'subscription' ? 'Not metered' : 'No charge')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bd sub" style={{ color: 'var(--sub)', fontSize: 12.5, borderTop: '1px solid var(--line)' }}>
            {int(u.data.totals.calls)} calls · {usd(u.data.totals.metered_cost_usd)} metered · {int(u.data.totals.not_metered_calls)} not metered
            (Claude CLI on the subscription, counted but never priced as $0).
          </div>
        </>
      )}
    </div>
  );
}

/** For the Settings tab head: while a test is running, say so. */
export const llmBusy = anyRunning;
